import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { WidgetGroup } from "@insite/client-framework/Types/WidgetGroups";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    subheading = "subheading",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.subheading]: string;
    };
}

export interface GAIAMYHTestingResultsStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    completeTestingWrapper?: InjectableCss;
    assuranceWrapper?: InjectableCss;
}

const styles: GAIAMYHTestingResultsStyles = {
    mainGridContainer: {
        css: css`
            background: #f0f4f3;
            padding: 14px 14px 62px 21px;
            border-radius: 5px;
            margin-top: 33px;
            > ${GridOffset} {
                margin: 0 !important;
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [null, null, null, null, null])}
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            align-items: center;
            padding: 0 0 12px;
            border-bottom: 1px solid #ccc;
            margin-bottom: 10px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    titleLabel: {
        variant: "h2",
        css: css`
            margin-bottom: 0 !important;
            font-size: 22px;
            font-family: "SaboNextLtPro";
            font-weight: 600;
            color: #183028;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 20px 0 14px !important;
                    `,
                    css`
                        margin: 20px 0 14px !important;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    descriptionText: {
        css: css`
            > p {
                font-family: Lato;
                margin-bottom: 19px;
                color: ${getColor("#333333")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > p {
                            margin-bottom: 20px;
                        }
                    `,
                    css`
                        > p {
                            margin-bottom: 20px;
                        }
                    `,
                    css`
                        > p {
                            margin-bottom: 30px;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-wrap: wrap;
                    `,
                    css`
                        flex-wrap: wrap;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                ])}
        `,
    },
    completeTestingWrapper: {
        css: css`
            width: 48%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                ])}
        `,
    },
    assuranceWrapper: {
        css: css`
            width: 48%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                ])}
        `,
    },
};

type Props = OwnProps;

export const GAIAMYHTestingResults = ({ fields, id }: Props) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const onChangeModelHandler = () => {
        setModalIsOpen(true);
    };
    const handleCloseModal = () => {
        setModalIsOpen(false);
    };
    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleLabel}>{fields.heading}</Typography>
                <StyledWrapper>
                    <Zone contentId={id} zoneName="Content" />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.descriptionGridItem}>
                <Typography {...styles.descriptionText}>{parse(fields.subheading, parserOptions)}</Typography>
            </GridItem>
            <StyledWrapper {...styles.contentWrapper}>
                <StyledWrapper {...styles.completeTestingWrapper}>
                    <Zone contentId={id} zoneName="Content1" />
                </StyledWrapper>
                <StyledWrapper {...styles.assuranceWrapper}>
                    <Zone contentId={id} zoneName="Content2" />
                </StyledWrapper>
            </StyledWrapper>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: GAIAMYHTestingResults,
    definition: {
        displayName: "MYH Test Result",
        group: "MYH Product Details" as WidgetGroup,
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "Testing Results",
                fieldType: "Translatable",
            },
            {
                name: fields.subheading,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue:
                    "<p>We believe you deserve to know exactly what’s in your supplements—and what’s not. At Gaia Herbs, every ingredient and finished product is tested in our state-of-the-art laboratory to ensure Purity, Integrity, and Potency.</p>",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
